import React, { useState, useCallback } from 'react'
import { Network, ChevronRight, ChevronLeft, RotateCcw } from 'lucide-react'
import Step1VcnCount from './components/Step1VcnCount'
import Step2SubnetCount from './components/Step2SubnetCount'
import Step3BaseCidr from './components/Step3BaseCidr'
import Step4Calculator from './components/Step4Calculator'
import DisclaimerModal from './components/DisclaimerModal'
import { createDynamicTopologyDef, proposeCidrs, calculateVlsmSubnets, calculateSmartVcnCidrForVlsm, calculateSmartVcnCidrForEqualSize, proposeSubnetsForVcn } from './utils/cidrCalculator'
import { parseCidr } from './utils/cidrUtils'
import './App.css'

const TOTAL_STEPS = 4

function App() {
  const [showDisclaimer, setShowDisclaimer] = useState(true)
  const [currentStep, setCurrentStep] = useState(1)
  const [config, setConfig] = useState({
    vcnCount: 1,
    subnetCounts: [],
    baseCidrs: [],
    vcnNames: [],
    subnetHosts: null, // VLSM host counts per subnet
    useFullBaseCidr: false // Use full base CIDR instead of optimized size
  })
  const [calculatorResult, setCalculatorResult] = useState(null)

  const handleStep1Complete = useCallback((vcnCount) => {
    setConfig(prev => ({
      ...prev,
      vcnCount,
      subnetCounts: new Array(vcnCount).fill(2),
      baseCidrs: new Array(vcnCount).fill('10.0.0.0/16'),
      vcnNames: new Array(vcnCount).fill('').map((_, i) => `VCN ${i + 1}`)
    }))
    setCurrentStep(2)
  }, [])

  const handleStep2Complete = useCallback((subnetCounts, vcnNames, subnetHosts, useVlsm) => {
    setConfig(prev => ({
      ...prev,
      subnetCounts,
      vcnNames,
      subnetHosts,
      useVlsm
    }))
    setCurrentStep(3)
  }, [])

  const handleStep3Complete = useCallback((baseCidrs, useFullBaseCidr = false) => {
    setConfig(prev => {
      const newConfig = {
        ...prev,
        baseCidrs,
        useFullBaseCidr
      }

      // Generate the calculator result
      const topologyDef = createDynamicTopologyDef(
        newConfig.vcnCount,
        newConfig.subnetCounts,
        baseCidrs
      )

      let plan, errors

      // Use VLSM calculation if host counts are specified
      if (newConfig.subnetHosts) {
        plan = []
        errors = []

        for (let vi = 0; vi < topologyDef.vcns.length; vi++) {
          const vcn = topologyDef.vcns[vi]
          const baseCidr = baseCidrs[vi] || '10.0.0.0/16'
          const vcnPlan = { vcnCidr: '', subnetCidrs: [] }

          if (useFullBaseCidr) {
            // Use full base CIDR as VCN CIDR
            vcnPlan.vcnCidr = baseCidr
            vcnPlan.useFullBaseCidr = true

            // Then calculate VLSM subnets
            if (vcn.subnets.length > 0) {
              const subnetDefs = vcn.subnets.map((subnet, si) => ({
                label: subnet.label,
                hosts: newConfig.subnetHosts[vi]?.[si] || 253
              }))

              const { subnetCidrs, error, warnings } = calculateVlsmSubnets(vcnPlan.vcnCidr, subnetDefs)
              vcnPlan.subnetCidrs = subnetCidrs
              vcnPlan.vlsmWarnings = warnings || []

              // Calculate utilization statistics
              const baseParsed = parseCidr(baseCidr)
              const totalBaseIps = baseParsed ? (baseParsed.end - baseParsed.start + 1) >>> 0 : 0
              let usedIps = 0
              subnetCidrs.forEach(cidr => {
                const parsed = parseCidr(cidr)
                if (parsed) {
                  usedIps += (parsed.end - parsed.start + 1) >>> 0
                }
              })
              vcnPlan.utilizationStats = {
                totalBaseIps,
                usedIps,
                wastedIps: totalBaseIps - usedIps,
                utilizationPercent: totalBaseIps > 0 ? ((usedIps / totalBaseIps) * 100).toFixed(1) : 0
              }

              if (error) {
                errors.push({ type: 'vcn', vcnIndex: vi, message: error })
              }
            }
          } else {
            // Calculate smart VCN CIDR based on actual VLSM subnet requirements
            const hostRequirements = vcn.subnets.map((subnet, si) => ({
              hosts: newConfig.subnetHosts[vi]?.[si] || 253
            }))

            const { vcnCidr, prefix, expanded, reason, error: vcnError } = calculateSmartVcnCidrForVlsm(baseCidr, hostRequirements)

            if (!vcnCidr || vcnError) {
              errors.push({
                type: 'vcn',
                vcnIndex: vi,
                message: vcnError || `VCN ${vi + 1}: Cannot calculate VCN CIDR. ${reason || 'Unknown error'}`
              })
              plan.push(vcnPlan)
              continue
            }

            vcnPlan.vcnCidr = vcnCidr

            // Log expansion info for debugging
            if (expanded && reason) {
              console.log(`VLSM VCN ${vi + 1}: ${reason}`)
            }

            // Then calculate VLSM subnets
            if (vcn.subnets.length > 0) {
              const subnetDefs = vcn.subnets.map((subnet, si) => ({
                label: subnet.label,
                hosts: newConfig.subnetHosts[vi]?.[si] || 253
              }))

              const { subnetCidrs, error, warnings } = calculateVlsmSubnets(vcnPlan.vcnCidr, subnetDefs)
              vcnPlan.subnetCidrs = subnetCidrs
              vcnPlan.vlsmWarnings = warnings || []

              if (error) {
                errors.push({ type: 'vcn', vcnIndex: vi, message: error })
              }
            }
          }

          plan.push(vcnPlan)
        }
      } else {
        // Use standard equal-size calculation with smart VCN sizing
        plan = []
        errors = []

        for (let vi = 0; vi < topologyDef.vcns.length; vi++) {
          const vcn = topologyDef.vcns[vi]
          const baseCidr = baseCidrs[vi] || '10.0.0.0/16'
          const vcnPlan = { vcnCidr: '', subnetCidrs: [] }

          if (useFullBaseCidr) {
            // Use full base CIDR as VCN CIDR
            vcnPlan.vcnCidr = baseCidr
            vcnPlan.useFullBaseCidr = true

            // Calculate equal-size subnets within the full base CIDR
            if (vcn.subnets.length > 0) {
              const subnetCount = vcn.subnets.length
              const vcnParsed = parseCidr(baseCidr)

              // Determine subnet prefix based on count
              let subnetPrefix
              if (subnetCount <= 8) subnetPrefix = 24
              else if (subnetCount <= 16) subnetPrefix = 25
              else if (subnetCount <= 32) subnetPrefix = 26
              else subnetPrefix = 27

              // Ensure subnet prefix is valid within VCN
              subnetPrefix = Math.max(subnetPrefix, vcnParsed.prefix + 1)

              // Generate subnet CIDRs
              const { subnetCidrs, error } = proposeSubnetsForVcn(vcnPlan.vcnCidr, subnetCount)
              vcnPlan.subnetCidrs = subnetCidrs

              // Calculate utilization statistics
              const totalBaseIps = (vcnParsed.end - vcnParsed.start + 1) >>> 0
              let usedIps = 0
              subnetCidrs.forEach(cidr => {
                const parsed = parseCidr(cidr)
                if (parsed) {
                  usedIps += (parsed.end - parsed.start + 1) >>> 0
                }
              })
              vcnPlan.utilizationStats = {
                totalBaseIps,
                usedIps,
                wastedIps: totalBaseIps - usedIps,
                utilizationPercent: totalBaseIps > 0 ? ((usedIps / totalBaseIps) * 100).toFixed(1) : 0
              }

              if (error) {
                errors.push({ type: 'vcn', vcnIndex: vi, message: error })
              }
            }
          } else {
            // Calculate smart VCN CIDR based on number of subnets
            const { vcnCidr, expanded, reason } = calculateSmartVcnCidrForEqualSize(baseCidr, vcn.subnets.length)

            if (!vcnCidr) {
              errors.push({
                type: 'vcn',
                vcnIndex: vi,
                message: `VCN ${vi + 1}: Cannot calculate VCN CIDR. ${reason || 'Unknown error'}`
              })
              plan.push(vcnPlan)
              continue
            }

            vcnPlan.vcnCidr = vcnCidr

            // Log expansion info for debugging
            if (expanded && reason) {
              console.log(`Equal Size VCN ${vi + 1}: ${reason}`)
            }

            // Calculate equal-size subnets within the optimized VCN
            if (vcn.subnets.length > 0) {
              const subnetCount = vcn.subnets.length
              const vcnParsed = parseCidr(vcnPlan.vcnCidr)

              // Determine subnet prefix based on count
              let subnetPrefix
              if (subnetCount <= 8) subnetPrefix = 24
              else if (subnetCount <= 16) subnetPrefix = 25
              else if (subnetCount <= 32) subnetPrefix = 26
              else subnetPrefix = 27

              // Ensure subnet prefix is valid within VCN
              subnetPrefix = Math.max(subnetPrefix, vcnParsed.prefix + 1)

              // Generate subnet CIDRs
              const { subnetCidrs, error } = proposeSubnetsForVcn(vcnPlan.vcnCidr, subnetCount)
              vcnPlan.subnetCidrs = subnetCidrs

              if (error) {
                errors.push({ type: 'vcn', vcnIndex: vi, message: error })
              }
            }
          }

          plan.push(vcnPlan)
        }
      }

      setCalculatorResult({
        topologyDef,
        plan,
        errors,
        config: newConfig
      })

      setCurrentStep(4)

      return newConfig
    })
  }, [])

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }, [currentStep])

  const handleReset = useCallback(() => {
    if (confirm('Start over? This will reset all configuration.')) {
      setCurrentStep(1)
      setConfig({
        vcnCount: 1,
        subnetCounts: [],
        baseCidrs: [],
        vcnNames: [],
        subnetHosts: null,
        useFullBaseCidr: false
      })
      setCalculatorResult(null)
    }
  }, [])

  const handleAcceptDisclaimer = useCallback(() => {
    setShowDisclaimer(false)
  }, [])

  const handleDeclineDisclaimer = useCallback(() => {
    window.location.href = 'about:blank'
  }, [])

  const renderStepIndicator = () => {
    return (
      <div className="step-indicator">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => (
          <div
            key={i}
            className={`step-dot ${i + 1 === currentStep ? 'active' : ''} ${
              i + 1 < currentStep ? 'completed' : ''
            }`}
          >
            <span className="step-number">{i + 1}</span>
            <span className="step-label">
              {i === 0 && 'VCNs'}
              {i === 1 && 'Subnets'}
              {i === 2 && 'Base CIDRs'}
              {i === 3 && 'Results'}
            </span>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="app">
      {showDisclaimer && (
        <DisclaimerModal
          onAccept={handleAcceptDisclaimer}
          onDecline={handleDeclineDisclaimer}
        />
      )}
      <header className="app-header">
        <div className="header-content">
          <div className="header-branding">
            <Network size={28} className="header-icon" />
            <div>
              <h1>OCI VCN CIDR Calculator</h1>
              <p className="header-subtitle">Plan your Oracle Cloud Infrastructure network</p>
            </div>
          </div>
          {currentStep > 1 && (
            <button className="btn-reset" onClick={handleReset}>
              <RotateCcw size={16} />
              Start Over
            </button>
          )}
        </div>
      </header>

      <main className="app-main">
        {renderStepIndicator()}

        <div className="step-container">
          {currentStep === 1 && (
            <Step1VcnCount
              initialCount={config.vcnCount}
              onComplete={handleStep1Complete}
            />
          )}

          {currentStep === 2 && (
            <Step2SubnetCount
              vcnCount={config.vcnCount}
              initialSubnetCounts={config.subnetCounts}
              initialVcnNames={config.vcnNames}
              onComplete={handleStep2Complete}
              onBack={handleBack}
            />
          )}

          {currentStep === 3 && (
            <Step3BaseCidr
              vcnCount={config.vcnCount}
              vcnNames={config.vcnNames}
              subnetCounts={config.subnetCounts}
              initialBaseCidrs={config.baseCidrs}
              useVlsm={config.useVlsm}
              onComplete={handleStep3Complete}
              onBack={handleBack}
            />
          )}

          {currentStep === 4 && calculatorResult && (
            <Step4Calculator
              result={calculatorResult}
              onBack={handleBack}
              onReset={handleReset}
            />
          )}
        </div>
      </main>

      <footer className="app-footer">
        <p>OCI VCN CIDR Calculator by <a href="http://www.iwanhoogendoorn.nl/" target="_blank" rel="noopener noreferrer">Iwan Hoogendoorn</a></p>
      </footer>
    </div>
  )
}

export default App
